import { MealPlanAgent } from "@corpuscomplete/core/nutrition/meal-plan-agent";
import { MessagePart } from "@corpuscomplete/web/modules/common/agent-chat/message-part";
import { ChatView } from "@corpuscomplete/web/modules/common/agent-chat/session-chat-view";
import { AgentChat } from "@corpuscomplete/core-ui/ai/agent/chat";
import { AgentTools } from "@corpuscomplete/core-ui/ai/agent/tools";
import { MealPlanDetail } from "@corpuscomplete/core-ui/nutrition/meal-plan/detail";
import { ToolError } from "@corpuscomplete/web/modules/common/agent-chat";
import { WebSocket } from "partysocket";
import { Env } from "@corpuscomplete/web/env";
import { ToolProposePlanPending, ToolProposePlanCompleted } from "./tools";
import {
  ToolAskQuestionsCompleted,
  ToolAskQuestionsPending,
} from "@corpuscomplete/web/modules/common/agent-chat/tools/ask-questions";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Link } from "@corpuscomplete/web/components/link";
import { InfoIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@corpuscomplete/web-ui/components/card";
import { Progress } from "@corpuscomplete/web-ui/components/progress";

const createWebSocket = (path: string): AgentChat.WebSocketLike => {
  const apiUrl = new URL(Env.VITE_API_URL);
  const wsUrl = `${apiUrl.protocol === "https:" ? "wss:" : "ws:"}//${apiUrl.host}${path}`;
  const socket = new WebSocket(wsUrl);

  return {
    addEventListener: socket.addEventListener.bind(socket),
    send: socket.send.bind(socket),
    close: socket.close.bind(socket),
    get readyState() {
      return socket.readyState as 0 | 1 | 2 | 3;
    },
  };
};

/**
 * Meal-plan planning chat shell for agent sessions.
 * Contract: wires AgentChat transport/session ids and delegates policy/state
 * decisions to @corpuscomplete/core-ui agent + MealPlanDetail contracts.
 */
export function MealPlanAgentPlanningChat({
  userId,
  agentVersion,
  sessionId,
  unavailabilityReason,
  canEditSession,
}: {
  userId: string;
  agentVersion: number;
  sessionId: string;
  unavailabilityReason: MealPlanAgent.UnavailableReason | null;
  canEditSession: boolean;
}) {
  const mealGenerationSummary = MealPlanDetail.useMealWorkflowProgressSummary();

  return (
    <AgentChat.Provider
      userId={userId}
      agentType={MealPlanAgent.definition.agentType}
      agentVersion={agentVersion}
      sessionId={sessionId}
      canEditSession={canEditSession}
      createWebSocket={createWebSocket}
    >
      <div className="flex h-full flex-col">
        <NutritionContextWarning reason={unavailabilityReason} />
        {mealGenerationSummary && <MealGenerationSummaryCard summary={mealGenerationSummary} />}
        <ChatView<MealPlanAgent.PlanningUIMessage>
          className="min-h-0 flex-1"
          renderUIMessage={(message) => <Message message={message} />}
          deferredToolPresenter={MealPlanDetail.deferredToolPresenter}
          renderPendingAction={(part, info) => <PendingAction part={part} info={info} />}
        />
      </div>
    </AgentChat.Provider>
  );
}

function MealGenerationSummaryCard({
  summary,
}: {
  summary: MealPlanDetail.MealWorkflowProgressSummary;
}) {
  const statusText =
    summary.completedMeals === summary.totalMeals
      ? "Meal generation complete."
      : summary.hasActiveWork
        ? "Generating meals..."
        : "Waiting on remaining meals.";

  return (
    <div className="px-3 pt-3">
      <Card size="sm">
        <CardHeader>
          <CardTitle className="text-base">Meal generation</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <Text variant="xs" className="font-medium">
              {summary.completedMeals} / {summary.totalMeals} meals complete
            </Text>
            <Text variant="xs" className="text-muted-foreground">
              {summary.progressPercent}%
            </Text>
          </div>
          <Progress value={summary.progressPercent} />
          <Text variant="xs" className="text-muted-foreground">
            {statusText}
          </Text>
        </CardContent>
      </Card>
    </div>
  );
}

function NutritionContextWarning({ reason }: { reason: MealPlanAgent.UnavailableReason | null }) {
  const warning = MealPlanDetail.getChatUnavailabilityWarningContent(reason);

  if (warning === null) {
    return null;
  }

  return (
    <div className="mx-3 mt-3 flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-muted-foreground">
      <InfoIcon className="size-3.5 shrink-0" />
      <Text variant="xs">
        {warning.message}
        {warning.showProgramsLink && (
          <>
            {" "}
            <Link to="/personal/programs" className="underline underline-offset-2">
              {warning.programsLinkLabel}
            </Link>{" "}
            {warning.programsLinkSuffix.trimStart()}
          </>
        )}
      </Text>
    </div>
  );
}

function PendingAction({
  part,
  info,
}: {
  part: AgentTools.DeferredTool;
  info: AgentTools.PendingActionRenderInfo & { isInteractive?: boolean };
}) {
  switch (part.type) {
    case "tool-proposePlan":
      if (!MealPlanDetail.isProposePlanPart(part)) {
        return null;
      }
      return (
        <ToolProposePlanPending
          messageId={info.messageId}
          isActive={info.isActive}
          isPersisted={info.isPersisted}
          isQueueInteractive={info.isInteractive ?? (info.isActive && info.isPersisted)}
          queuePosition={info.queuePosition}
          queueTotal={info.queueTotal}
          upNextLabel={info.upNextLabel}
          part={part}
        />
      );
    case "tool-askQuestions":
      if (!MealPlanDetail.isAskQuestionsPart(part)) {
        return null;
      }
      return (
        <ToolAskQuestionsPending
          messageId={info.messageId}
          isActive={info.isActive}
          isPersisted={info.isPersisted}
          isQueueInteractive={info.isInteractive ?? (info.isActive && info.isPersisted)}
          queuePosition={info.queuePosition}
          queueTotal={info.queueTotal}
          upNextLabel={info.upNextLabel}
          part={part}
        />
      );
    default:
      return null;
  }
}

function Message({
  message,
}: {
  message: AgentChat.UIMessageWithStatus<MealPlanAgent.PlanningUIMessage>;
}) {
  return (
    <>
      {message.parts.map((part, i) => {
        if ("state" in part && part.state === "output-error") {
          return <ToolError key={i} errorText={part.errorText} />;
        }

        switch (part.type) {
          case "step-start":
            return null;
          case "text":
            return (
              <MessagePart.Text
                key={i}
                role={message.role}
                part={part}
                isStreaming={message.status === "streaming"}
              />
            );
          case "tool-proposePlan":
            if (MealPlanDetail.isProposePlanCompletedPart(part)) {
              return <ToolProposePlanCompleted key={i} part={part} />;
            }
            return null;
          case "tool-askQuestions":
            if (MealPlanDetail.isAskQuestionsCompletedPart(part)) {
              return <ToolAskQuestionsCompleted key={i} part={part} />;
            }
            return null;
          default:
            console.warn("unknown part type", part.type, message);
            return null;
        }
      })}
    </>
  );
}
